import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../../components/Icon';
import { Kbd } from '../../components/Kbd';
import { hasModifier, useModifierLabel } from '../../lib/keyboard';
import { useDebounced } from '../../lib/useDebounced';
import { NAVIGATION } from '../../app/navigation';
import { PALETTE_OPEN_EVENT } from './open';
import { useSession } from '../auth/queries';
import { MIN_SEARCH_LENGTH, useCustomerSearch, useTicketSearch } from './queries';

/**
 * Die Befehlspalette -- ein Feld fuer alles. F1-43, K-29, K-208.
 *
 * Seite wechseln, Ticket oeffnen, Kunde finden: drei Buchstaben statt vier
 * Klicks. Sie ersetzt die Navigation nicht -- sie erspart den Weg dorthin.
 * Wer sie nicht kennt, sieht sie nie und verliert nichts; das Kuerzel steht
 * deshalb sichtbar in der Kopfleiste und nicht nur im Handbuch.
 *
 * **Was sie zeigt, richtet sich nach dem, was im Feld steht.** Leer: die
 * Ziele der Anwendung, damit die Palette beim ersten Oeffnen erklaert, was
 * sie kann. Ab zwei Zeichen zusaetzlich Tickets und Kunden vom Server. Ein
 * einzelner Buchstabe trifft alles und kostet eine Abfrage ueber den ganzen
 * Index -- fuer eine Trefferliste, die niemandem hilft.
 *
 * **Sie zeigt nur, was der Betrachter ohnehin sehen darf.** Die Ziele sind
 * nach demselben Recht gefiltert wie die Navigation, und Tickets und Kunden
 * kommen aus denselben Endpunkten wie die Listen -- mit derselben
 * Mandantentrennung. Die Palette ist eine Abkuerzung, kein zweiter Weg an
 * den Daten vorbei.
 *
 * Bedient wird sie mit Pfeiltasten und Eingabetaste. Die Maus geht auch;
 * hervorgehoben ist aber immer, was die Tastatur gewaehlt hat -- sonst
 * spraenge die Auswahl, sobald der Zeiger zufaellig ueber der Liste liegt.
 */

interface PaletteItem {
  id: string;
  /** Was in der Zeile steht. */
  label: string;
  /** Rechts daneben: Ticketnummer, Kundenstatus, Art des Ziels. */
  meta?: string;
  icon: IconName;
  /** Zu welcher Ueberschrift die Zeile gehoert. */
  group: string;
  run: () => void;
}

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const modifier = useModifierLabel();

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Der Suchbegriff, der beim Server ankommt. Verzoegert, weil sonst jeder
  // Tastendruck eine Volltextsuche ausloest -- "Drucker" waeren sieben.
  const search = useDebounced(term.trim(), 180);

  const tickets = useTicketSearch(search);
  const customers = useCustomerSearch(search);

  const permissions = useMemo(() => session.data?.permissions ?? [], [session.data]);

  // ---- Oeffnen und Schliessen -------------------------------------------
  // Am Fenster und nicht an einem Element: Das Kuerzel gilt ueberall, auch
  // waehrend der Zeiger in einem Textfeld steht. Ausgenommen ist nichts --
  // die Zusatztaste macht es eindeutig.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (hasModifier(event) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    const onOpenRequest = () => {
      setOpen(true);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(PALETTE_OPEN_EVENT, onOpenRequest);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(PALETTE_OPEN_EVENT, onOpenRequest);
    };
  }, []);

  // Bei jedem Oeffnen von vorn: Ein Feld, in dem noch die Suche von gestern
  // steht, ist eine Falle -- die Eingabetaste fuehrte woandershin als
  // erwartet.
  useEffect(() => {
    if (!open) return;
    setTerm('');
    setActive(0);
    // Nach dem Erscheinen, nicht davor: Vorher gibt es das Feld noch nicht.
    const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [open]);

  // ---- Was in der Liste steht -------------------------------------------
  const items = useMemo<PaletteItem[]>(() => {
    const needle = term.trim().toLowerCase();

    const targets: PaletteItem[] = NAVIGATION.filter(
      (entry) => entry.permission === undefined || permissions.includes(entry.permission),
    )
      .map((entry) => ({
        id: `nav:${entry.to}`,
        label: t(entry.labelKey),
        meta: t('palette.groupNavigate'),
        icon: entry.icon,
        group: t('palette.groupNavigate'),
        run: () => {
          void navigate({ to: entry.to });
        },
      }))
      // Die Ziele werden im Browser gefiltert und nicht beim Server: Es sind
      // zwanzig, sie stehen ohnehin schon da, und eine Abfrage dafuer waere
      // eine Abfrage fuer nichts.
      .filter((entry) => needle === '' || entry.label.toLowerCase().includes(needle));

    const ticketItems: PaletteItem[] = (tickets.data?.items ?? []).map((ticket) => ({
      id: `ticket:${ticket.id}`,
      label: ticket.title,
      meta: ticket.ticket_number,
      icon: 'tickets',
      group: t('palette.groupTickets'),
      run: () => {
        void navigate({ to: '/app/tickets/$ticketId', params: { ticketId: ticket.id } });
      },
    }));

    const customerItems: PaletteItem[] = (customers.data?.items ?? []).map((customer) => ({
      id: `customer:${customer.id}`,
      label: customer.name,
      meta: t(`customerStatus.${customer.status}`),
      icon: 'crm',
      group: t('palette.groupCustomers'),
      run: () => {
        void navigate({ to: '/app/crm/$customerId', params: { customerId: customer.id } });
      },
    }));

    // Tickets zuerst: Wer die Palette waehrend der Arbeit oeffnet, sucht in
    // neun von zehn Faellen einen Vorgang.
    return [...ticketItems, ...customerItems, ...targets];
  }, [term, permissions, tickets.data, customers.data, navigate, t]);

  // Die Auswahl darf nicht hinter das Ende der Liste rutschen, wenn ein
  // Suchergebnis eintrifft und die Liste kuerzer wird.
  useEffect(() => {
    setActive((current) => (current >= items.length ? 0 : current));
  }, [items.length]);

  if (!open) return null;

  const choose = (index: number) => {
    const item = items[index];
    if (!item) return;
    setOpen(false);
    item.run();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((current) => (items.length === 0 ? 0 : (current + 1) % items.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((current) =>
          items.length === 0 ? 0 : (current - 1 + items.length) % items.length,
        );
        break;
      case 'Enter':
        event.preventDefault();
        choose(active);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
    }
  };

  const loading = tickets.isFetching || customers.isFetching;

  return (
    <>
      {/* Der Schleier schliesst beim Klick. Er traegt keine Beschriftung und
          keine Rolle -- der Weg mit der Tastatur ist die Fluchttaste. */}
      <div
        className="palette__overlay"
        onClick={() => {
          setOpen(false);
        }}
        aria-hidden="true"
      />
      <div
        className="palette__content"
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        onKeyDown={onKeyDown}
      >
        <div className="palette__search">
          <Icon name="search" />
          <input
            ref={inputRef}
            className="palette__input"
            type="text"
            value={term}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={items[active] ? `palette-item-${items[active].id}` : undefined}
            aria-label={t('palette.searchLabel')}
            placeholder={t('palette.placeholder')}
            autoComplete="off"
            onChange={(event) => {
              setTerm(event.target.value);
              setActive(0);
            }}
          />
          {loading ? <span className="spinner" aria-hidden="true" /> : null}
        </div>

        <ul className="palette__list" id="palette-list" role="listbox" ref={listRef}>
          {items.length === 0 ? (
            <li className="palette__empty">
              {term.trim().length < MIN_SEARCH_LENGTH
                ? t('palette.typeMore', { count: MIN_SEARCH_LENGTH })
                : t('palette.noResults')}
            </li>
          ) : null}

          {items.map((item, index) => (
            <li key={item.id}>
              {/* Die Ueberschrift steht in der Zeile davor und nicht als
                  eigener Listeneintrag: Ein ``role="listbox"`` darf nur
                  Optionen enthalten, sonst zaehlt ein vorlesendes Programm
                  Ueberschriften als Treffer mit. */}
              {index === 0 || items[index - 1]?.group !== item.group ? (
                <p className="palette__group" aria-hidden="true">
                  {item.group}
                </p>
              ) : null}
              <button
                type="button"
                id={`palette-item-${item.id}`}
                role="option"
                aria-selected={index === active}
                data-active={index === active}
                className="palette__item"
                tabIndex={-1}
                onMouseMove={() => {
                  setActive(index);
                }}
                onClick={() => {
                  choose(index);
                }}
              >
                <Icon name={item.icon} />
                <span className="palette__label">{item.label}</span>
                {item.meta ? <span className="palette__meta">{item.meta}</span> : null}
              </button>
            </li>
          ))}
        </ul>

        <div className="palette__footer">
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> {t('palette.hintMove')}
          </span>
          <span>
            <Kbd>↵</Kbd> {t('palette.hintOpen')}
          </span>
          <span>
            <Kbd>{t('shortcuts.escapeKey')}</Kbd> {t('palette.hintClose')}
          </span>
          <span>
            <Kbd>{modifier}</Kbd> <Kbd>K</Kbd> {t('palette.hintToggle')}
          </span>
        </div>
      </div>
    </>
  );
}
